// ===========================================
// Admin event Preview
// ===========================================
// Renders the REAL public page body and the REAL parent page body for any event, drafts included, so an
// admin sees what a family or the public will see. Read-only by construction: both bodies get `preview`,
// which turns off buying, registering, RSVP and the calendar links, and the data comes from one admin read
// (`GET /admin/events/:id/preview`) that writes nothing and holds no seats.
// The banner says what the chosen audience ACTUALLY gets (`previewAudienceNotes`): for a closed, staff-only
// or targeted event that can be less than the page below, or nothing at all.
// Route: /:schoolSlug/admin/events/:id/preview?as=public|family

import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { useEvent, useEventPreview } from '../../hooks/useEvents';
import { useTenant } from '../../contexts/TenantContext';
import { previewAudienceNotes } from '../../components/events/eventAudience';
import { PublicEventView } from '../public/PublicEventDetailPage';
import { ParentEventDetailView } from '../parent/ParentEventDetailPage';
import type { EventPreviewView } from '../../api/events.api';

const asView = (value: string | null): EventPreviewView | null =>
  value === 'public' || value === 'family' ? value : null;

export default function EventPreviewPage() {
  const { id = '' } = useParams<{ id: string }>();
  const { schoolSlug } = useTenant();
  const [searchParams, setSearchParams] = useSearchParams();

  // The admin read says whether this event has a public page at all (isPublic), which decides the default view,
  // and who it is shown to (visibility), which the banner states. Same `events.read` gate as the preview.
  const adminEvent = useEvent(id);
  const isPublic = adminEvent.data?.isPublic;
  const visibility = adminEvent.data?.visibility;

  const requested = asView(searchParams.get('as'));
  // A non-public event has no public page, so it can only be previewed as families see it.
  const view: EventPreviewView | null =
    isPublic === undefined ? null : requested === 'public' && isPublic ? 'public' : requested === 'family' ? 'family' : isPublic ? 'public' : 'family';

  const preview = useEventPreview(id, view ?? 'family', view !== null);

  const backToEvent = `/${schoolSlug}/admin/events/${id}`;
  const back = (
    <Button component={RouterLink} to={backToEvent} startIcon={<BackIcon />}>
      Back to event
    </Button>
  );

  if (adminEvent.isError || preview.isError) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          This event could not be loaded for a preview.
        </Alert>
        {back}
      </Box>
    );
  }

  // Only the first load shows a spinner; a view switch keeps the page (and this banner) on screen.
  if (view === null || !preview.data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress aria-label="Loading preview" />
      </Box>
    );
  }

  const data = preview.data;
  const switching = preview.isPlaceholderData === true;
  const notes = previewAudienceNotes(view, data.status, visibility, data.isPublic);

  return (
    <Box>
      <Alert
        severity="info"
        icon={false}
        // The message box grows to the full width, so the toggle sits at the right on a wide screen.
        sx={{ mb: 2, '& .MuiAlert-message': { flex: 1 } }}
        data-testid="preview-banner"
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ md: 'center' }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              {view === 'public'
                ? 'Preview: this is what the public sees'
                : 'Preview: this is what families see'}
            </Typography>
            <Typography variant="body2">
              Buying, registering and RSVP are turned off in a preview.
            </Typography>
            {notes.length > 0 && (
              <Box data-testid="preview-status-note">
                {notes.map((note) => (
                  <Typography key={note} variant="body2" fontWeight={600}>
                    {note}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={view}
              aria-label="Preview as"
              onChange={(_e, next: EventPreviewView | null) => {
                if (next) setSearchParams({ as: next }, { replace: true });
              }}
            >
              <ToggleButton value="family">Families</ToggleButton>
              <Tooltip title={isPublic ? '' : 'This event is not public, so it has no public page.'}>
                {/* A disabled button gets no pointer or focus events, so the span carries the tooltip, and
                    tabIndex lets a keyboard user reach the reason. */}
                <span tabIndex={isPublic ? undefined : 0}>
                  <ToggleButton value="public" disabled={!isPublic}>
                    Public
                  </ToggleButton>
                </span>
              </Tooltip>
            </ToggleButtonGroup>
            {back}
          </Stack>
        </Stack>
      </Alert>

      {switching && <LinearProgress aria-label="Loading preview" sx={{ mb: 2 }} />}

      {data.view === 'public' ? (
        <PublicEventView event={data.event} schoolSlug={schoolSlug ?? ''} preview />
      ) : (
        <ParentEventDetailView event={data.event} ownedTickets={[]} preview />
      )}
    </Box>
  );
}
